import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBase64 } from '../../../../assets/logoBase64';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

// Interfaz para los datos del PDF
export interface GuiaRecepcionPdfData {
  id: number;
  idGuiaProduccion?: number; // ID de la guía de producción para N° Orden
  numeroComprobante?: number;
  idDocumento?: number;
  documentoNumero?: string;
  idProveedor?: number;
  proveedorRazonSocial?: string;
  isServicio: boolean;
  observaciones?: string;
  notas?: string;
  fechaExtraccion: string;
  asociacion?: string;
  numeroMallas: number;
  numeroManojos?: number;
  placaVehiculo?: string;
  idEspecie?: number;
  especieDescripcion?: string;
  estado?: string;
  idOrigenProducto?: number;
  isEntero: boolean;
  detalles: GuiaRecepcionDetallePdf[];
  pesoMateriaPrima?: number; // Peso de materia prima para cálculo de rendimiento
  subLoteCantidad?: number; // Sub lote para rendimiento
}

export interface GuiaRecepcionDetallePdf {
  id: number;
  idArticulo: number;
  articuloCodigo?: string;
  articuloDescripcion?: string;
  cantidad: number;
  coeficiente?: number;
  descripcion?: string;
  idDuenio?: number;
  duenioNombre?: string;
  idEncargado?: number;
  encargadoNombre?: string;
  idPresentacion?: number;
  presentacionDescripcion?: string;
  calibre?: string;
  pesoEmpaqueInterior?: number;
}

interface CalibreGroup {
  codigo: string;
  cajas: number;
  bolsas: number;
  saldos: number;
  total: number;
}

interface PresentacionAgrupada {
  nombre: string;
  calibres: CalibreGroup[];
  totalCajas: number;
  totalBolsas: number;
  totalSaldos: number;
  totalPeso: number;
  pesoCaja?: number;   // Peso unitario de caja (ej: 20kg)
  pesoBolsa?: number;  // Peso unitario de bolsa (ej: 2kg)
}

// Calibres estándar en orden
const CALIBRES_STANDARD = ['10-20', '20-30', '30-40', '40-50', '50-60', '60-80', '80-100', '100-120', '120-over'];

export const guiaRecepcionPdfService = {
  /**
   * Genera el PDF del Reporte de Recepción
   */
  generatePdf(data: GuiaRecepcionPdfData): void {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = margin;

    // ==========================================
    // HEADER - Logo y título
    // ==========================================

    // Logo (izquierda)
    const logoWidth = 30;
    const logoHeight = 20;
    doc.addImage(logoBase64, 'PNG', margin, yPos, logoWidth, logoHeight);

    // Título central
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('GUIA DE PRODUCCION', pageWidth / 2, yPos + 10, { align: 'center' });

    // Empresa (derecha)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('CPP. ISABEL GOMEZ DE VIZA', pageWidth - margin, yPos + 10, { align: 'right' });

    yPos = margin + 22;

    // ==========================================
    // INFORMACIÓN DEL DERC
    // ==========================================
    doc.setFontSize(8);
    const col1X = margin;
    const col2X = pageWidth / 3;
    const col3X = (pageWidth / 3) * 2;

    // Fila 1: Fecha, N° Mallas, N° Orden + Sub Lote
    doc.setFont('helvetica', 'bold');
    doc.text('Fecha:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(data.fechaExtraccion), col1X + 18, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('N° Mallas:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(data.numeroMallas || 0), col2X + 22, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('N° Orden:', col3X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(data.idGuiaProduccion || data.id), col3X + 22, yPos);

    // Sub Lote al lado del N° Orden
    if (data.subLoteCantidad) {
      doc.setFont('helvetica', 'bold');
      doc.text('Sub Lote:', col3X + 45, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(data.subLoteCantidad), col3X + 65, yPos);
    }

    yPos += 5;

    // Fila 2: DERC, Asociación
    doc.setFont('helvetica', 'bold');
    doc.text('DERC:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.documentoNumero || 'N/A', col1X + 18, yPos);

    const isProductoB = data.idOrigenProducto === 2 && !data.isEntero;
    if (!isProductoB) {
      doc.setFont('helvetica', 'bold');
      doc.text('Asociación:', col2X, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(data.asociacion || 'N/A', col2X + 25, yPos);
    }

    yPos += 5;

    // Fila 3: Dueño y Proveedor
    const dueniosUnicos = [...new Set(data.detalles.filter(d => d.duenioNombre).map(d => d.duenioNombre))];
    const encargadosUnicos = [...new Set(data.detalles.filter(d => d.encargadoNombre).map(d => d.encargadoNombre))];

    doc.setFont('helvetica', 'bold');
    doc.text('Dueño:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(dueniosUnicos.join(', ') || 'N/A', col1X + 18, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('Proveedor:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.proveedorRazonSocial || 'N/A', col2X + 25, yPos);

    yPos += 5;

    // Fila 4: Encargado
    doc.setFont('helvetica', 'bold');
    doc.text('Encargado:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(encargadosUnicos.join(', ') || 'N/A', col1X + 25, yPos);

    yPos += 7;

    // ==========================================
    // TABLAS DE PRODUCTOS
    // ==========================================
    const productosAgrupados = agruparProductosPorPresentacion(data.detalles);
    const presentaciones = Object.keys(productosAgrupados);

    // Calcular anchos para las tablas
    const tableWidth = (pageWidth - margin * 2 - 10) / 2;
    let tableX = margin;
    let tableY = yPos;
    let maxYInRow = yPos;
    const tablesPerRow = 2;

    presentaciones.forEach((presentacion, index) => {
      const dataPresentacion = productosAgrupados[presentacion];

      // Si es una nueva fila
      if (index > 0 && index % tablesPerRow === 0) {
        tableX = margin;
        tableY = maxYInRow + 5;
      }

      // Crear tabla
      const tableEndY = renderProductTable(doc, tableX, tableY, tableWidth, dataPresentacion);

      maxYInRow = Math.max(maxYInRow, tableEndY);
      tableX += tableWidth + 5;
    });

    yPos = maxYInRow + 5;

    // ==========================================
    // TOTALES GENERALES
    // ==========================================
    // Verificar si necesitamos nueva página
    if (yPos > pageHeight - 35) {
      doc.addPage();
      yPos = margin;
    }

    // Calcular totales en kilos
    const totalGeneralKg = calcularTotalGeneralKg(data.detalles);
    // Rendimiento = Total Kilos / N° Sub Lote (o Mallas si no hay sub lote)
    const divisor = data.subLoteCantidad || data.numeroMallas || 1;
    const rendimiento = divisor > 0 ? (totalGeneralKg / divisor).toFixed(2) : '0.00';

    // Dibujar caja de totales
    doc.setDrawColor(0);
    doc.setFillColor(240, 240, 240);
    const boxWidth = 150;
    const boxX = pageWidth - margin - boxWidth;
    doc.rect(boxX, yPos, boxWidth, 18, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL GENERAL', boxX + 5, yPos + 7);
    doc.text(`${totalGeneralKg.toFixed(1)} Kg.`, boxX + boxWidth - 5, yPos + 7, { align: 'right' });

    doc.text('RENDIMIENTO (KG/MALLA)', boxX + 5, yPos + 14);
    doc.text(rendimiento, boxX + boxWidth - 5, yPos + 14, { align: 'right' });

    yPos += 25;

    // ==========================================
    // OBSERVACIONES Y FIRMA
    // ==========================================
    if (data.observaciones || data.notas) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('OBSERVACION:', margin, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const obsText = [data.observaciones, data.notas].filter(Boolean).join(' - ');
      const obsLines = doc.splitTextToSize(obsText, pageWidth - margin * 2);
      doc.text(obsLines, margin, yPos);
      yPos += obsLines.length * 4 + 5;
    }

    // Línea de firma
    const firmaY = Math.max(yPos + 5, pageHeight - 20);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.line(pageWidth / 2 - 30, firmaY, pageWidth / 2 + 30, firmaY);
    doc.text('FIRMA', pageWidth / 2, firmaY + 4, { align: 'center' });

    // ==========================================
    // NUMERACIÓN DE PÁGINAS
    // ==========================================
    const totalPages = doc.getNumberOfPages();
    if (totalPages > 1) {
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${i}/${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      }
    }

    // ==========================================
    // GUARDAR PDF
    // ==========================================
    const fileName = `Guia_Produccion_${data.numeroComprobante || data.id}_${formatDateForFileName(data.fechaExtraccion)}.pdf`;
    doc.save(fileName);
  }
};

/**
 * Renderiza una tabla de productos
 */
function renderProductTable(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  presentacion: PresentacionAgrupada
): number {
  const cellHeight = 6;
  const headerHeight = 8;
  let currentY = y;

  // Título de la tabla
  doc.setFillColor(200, 200, 200);
  doc.rect(x, currentY, width, headerHeight, 'F');
  doc.setDrawColor(0);
  doc.rect(x, currentY, width, headerHeight, 'S');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(presentacion.nombre.toUpperCase(), x + width / 2, currentY + 5.5, { align: 'center' });
  currentY += headerHeight;

  // Headers de columnas: COD | CAJAS KG | BOLSAS KG | SALDOS | TOTAL
  const colWidths = [width * 0.2, width * 0.2, width * 0.2, width * 0.2, width * 0.2];
  doc.setFillColor(240, 240, 240);
  doc.rect(x, currentY, width, cellHeight, 'F');
  doc.rect(x, currentY, width, cellHeight, 'S');

  doc.setFontSize(7);
  let colX = x;
  // Headers con peso unitario cuando disponible
  const cajaHeader = presentacion.pesoCaja ? `CAJAS x${presentacion.pesoCaja}` : 'CAJAS';
  const bolsaHeader = presentacion.pesoBolsa ? `BOLSAS x${presentacion.pesoBolsa}` : 'BOLSAS';
  const headers = ['COD', cajaHeader, bolsaHeader, 'SALDOS', 'TOTAL'];
  headers.forEach((header, i) => {
    doc.text(header, colX + colWidths[i] / 2, currentY + 4, { align: 'center' });
    if (i < headers.length - 1) {
      doc.line(colX + colWidths[i], currentY, colX + colWidths[i], currentY + cellHeight);
    }
    colX += colWidths[i];
  });
  currentY += cellHeight;

  // Filas de datos - solo mostrar calibres que tienen datos
  doc.setFont('helvetica', 'normal');

  // Ordenar calibres por el orden estándar y filtrar solo los que tienen datos
  const calibresConDatos = CALIBRES_STANDARD
    .map(calibre => presentacion.calibres.find(c => c.codigo === calibre))
    .filter((calibreData): calibreData is CalibreGroup => calibreData !== undefined && calibreData.total > 0);

  calibresConDatos.forEach(calibreData => {
    doc.rect(x, currentY, width, cellHeight, 'S');

    colX = x;
    // COD
    doc.text(calibreData.codigo, colX + colWidths[0] / 2, currentY + 4, { align: 'center' });
    doc.line(colX + colWidths[0], currentY, colX + colWidths[0], currentY + cellHeight);
    colX += colWidths[0];

    // CAJAS KG
    if (calibreData.cajas > 0) {
      doc.text(calibreData.cajas.toFixed(1), colX + colWidths[1] / 2, currentY + 4, { align: 'center' });
    }
    doc.line(colX + colWidths[1], currentY, colX + colWidths[1], currentY + cellHeight);
    colX += colWidths[1];

    // BOLSAS KG
    if (calibreData.bolsas > 0) {
      doc.text(calibreData.bolsas.toFixed(1), colX + colWidths[2] / 2, currentY + 4, { align: 'center' });
    }
    doc.line(colX + colWidths[2], currentY, colX + colWidths[2], currentY + cellHeight);
    colX += colWidths[2];

    // SALDOS
    if (calibreData.saldos > 0) {
      doc.text(calibreData.saldos.toFixed(1), colX + colWidths[3] / 2, currentY + 4, { align: 'center' });
    }
    doc.line(colX + colWidths[3], currentY, colX + colWidths[3], currentY + cellHeight);
    colX += colWidths[3];

    // TOTAL
    if (calibreData.total > 0) {
      doc.text(calibreData.total.toFixed(1), colX + colWidths[4] / 2, currentY + 4, { align: 'center' });
    }

    currentY += cellHeight;
  });

  // Fila de TOTAL
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(220, 220, 220);
  doc.rect(x, currentY, width, cellHeight, 'F');
  doc.rect(x, currentY, width, cellHeight, 'S');

  colX = x;
  doc.text('TOTAL', colX + colWidths[0] / 2, currentY + 4, { align: 'center' });
  doc.line(colX + colWidths[0], currentY, colX + colWidths[0], currentY + cellHeight);
  colX += colWidths[0];

  // Total CAJAS
  if (presentacion.totalCajas > 0) {
    doc.text(presentacion.totalCajas.toFixed(1), colX + colWidths[1] / 2, currentY + 4, { align: 'center' });
  }
  doc.line(colX + colWidths[1], currentY, colX + colWidths[1], currentY + cellHeight);
  colX += colWidths[1];

  // Total BOLSAS
  if (presentacion.totalBolsas > 0) {
    doc.text(presentacion.totalBolsas.toFixed(1), colX + colWidths[2] / 2, currentY + 4, { align: 'center' });
  }
  doc.line(colX + colWidths[2], currentY, colX + colWidths[2], currentY + cellHeight);
  colX += colWidths[2];

  // Total SALDOS
  if (presentacion.totalSaldos > 0) {
    doc.text(presentacion.totalSaldos.toFixed(1), colX + colWidths[3] / 2, currentY + 4, { align: 'center' });
  }
  doc.line(colX + colWidths[3], currentY, colX + colWidths[3], currentY + cellHeight);
  colX += colWidths[3];

  // Total GENERAL
  doc.text(presentacion.totalPeso.toFixed(1), colX + colWidths[4] / 2, currentY + 4, { align: 'center' });

  currentY += cellHeight;

  return currentY;
}

/**
 * Agrupa productos por presentación
 */
function agruparProductosPorPresentacion(
  detalles: GuiaRecepcionDetallePdf[]
): Record<string, PresentacionAgrupada> {
  const grupos: Record<string, PresentacionAgrupada> = {};

  detalles.forEach(detalle => {
    const { presentacion, calibre, tipo } = parseArticulo(detalle.articuloDescripcion || detalle.descripcion || '');

    if (!grupos[presentacion]) {
      grupos[presentacion] = {
        nombre: presentacion,
        calibres: [],
        totalCajas: 0,
        totalBolsas: 0,
        totalSaldos: 0,
        totalPeso: 0
      };
    }

    // Convertir a kilos: cantidad × pesoEmpaqueInterior
    const pesoEmpaque = detalle.pesoEmpaqueInterior || 1;
    const kilos = detalle.cantidad * pesoEmpaque;

    // Buscar o crear el calibre
    let calibreData = grupos[presentacion].calibres.find(c => c.codigo === calibre);
    if (!calibreData) {
      calibreData = { codigo: calibre, cajas: 0, bolsas: 0, saldos: 0, total: 0 };
      grupos[presentacion].calibres.push(calibreData);
    }

    // Determinar si es caja, bolsa o saldo basado en el tipo de artículo
    if (tipo === 'SALDOS') {
      calibreData.saldos += kilos;
      grupos[presentacion].totalSaldos += kilos;
    } else if (tipo === 'BOLSAS') {
      calibreData.bolsas += kilos;
      grupos[presentacion].totalBolsas += kilos;
      if (!grupos[presentacion].pesoBolsa && pesoEmpaque > 0) {
        grupos[presentacion].pesoBolsa = pesoEmpaque;
      }
    } else {
      // Por defecto, se considera como cajas
      calibreData.cajas += kilos;
      grupos[presentacion].totalCajas += kilos;
      if (!grupos[presentacion].pesoCaja && pesoEmpaque > 0) {
        grupos[presentacion].pesoCaja = pesoEmpaque;
      }
    }

    calibreData.total += kilos;
    grupos[presentacion].totalPeso += kilos;
  });

  return grupos;
}

/**
 * Extrae presentación, calibre y tipo del nombre del artículo
 */
function parseArticulo(articulo: string): { presentacion: string; calibre: string; tipo: string } {
  const articuloUpper = articulo.toUpperCase();

  // Extraer calibre
  const calibreMatch = articuloUpper.match(/(\d+[-]\d+|\d+[-]OVER)/i);
  const calibre = calibreMatch ? calibreMatch[1].toUpperCase() : 'OTROS';

  // Determinar presentación
  let presentacion = 'TALLO SOLO';
  if (articuloUpper.includes('MEDIA VALVA')) {
    if (articuloUpper.includes('TALLO CORAL')) {
      presentacion = 'TALLO CORAL MEDIA VALVA';
    } else {
      presentacion = 'TALLO SOLO MEDIA VALVA';
    }
  } else if (articuloUpper.includes('TALLO CORAL')) {
    presentacion = 'TALLO CORAL';
  }

  // Determinar tipo (cajas, bolsas, saldos) - orden de prioridad: SALDOS, BOLSAS, CAJAS
  let tipo = 'CAJAS';
  if (articuloUpper.includes('SALDO')) {
    tipo = 'SALDOS';
  } else if (articuloUpper.includes('BOLSA')) {
    tipo = 'BOLSAS';
  }

  return { presentacion, calibre, tipo };
}

/**
 * Calcula el total general de todos los productos en kilogramos
 */
function calcularTotalGeneralKg(detalles: GuiaRecepcionDetallePdf[]): number {
  return detalles.reduce((total, detalle) => {
    const pesoEmpaque = detalle.pesoEmpaqueInterior || 1;
    return total + (detalle.cantidad * pesoEmpaque);
  }, 0);
}

/**
 * Formatea fecha para mostrar
 */
function formatDate(dateString: string): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
}

/**
 * Formatea fecha para nombre de archivo
 */
function formatDateForFileName(dateString: string): string {
  if (!dateString) return 'fecha';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return 'fecha';
  }
}

export default guiaRecepcionPdfService;
